import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { useReducedMotion } from "framer-motion";
import {
  Lightbulb, PhoneCall, ListChecks, Network, Layers, Route, FileText,
  Code, Bug, MessageSquare, CloudUpload, HeartPulse, Check, Play, Rocket, RotateCcw,
} from "lucide-react";

const ICONS = [
  Lightbulb, PhoneCall, ListChecks, Network, Layers, Route,
  FileText, Code, Bug, MessageSquare, CloudUpload, HeartPulse,
];

// Desktop zigzag: row 1 runs forward, row 2 back, row 3 forward again.
const GRID = [
  [1, 1], [2, 1], [3, 1], [4, 1],
  [4, 2], [3, 2], [2, 2], [1, 2],
  [1, 3], [2, 3], [3, 3], [4, 3],
];

// Checkpoints sit on the connector after these nodes.
const CHECKPOINT_AFTER = [3, 7, 10];
const CHECKPOINT_ICONS = [Check, Play, Rocket];

const DURATION = 9000;

const WorkflowPath = ({ steps, phases, checkpoints, replayLabel, hint }) => {
  const reduceMotion = useReducedMotion();
  const wrapRef = useRef(null);
  const nodeRefs = useRef([]);
  const [geo, setGeo] = useState({ points: [], dists: [], total: 0, width: 0, height: 0 });
  const [progress, setProgress] = useState(0);
  const [selected, setSelected] = useState(0);
  const [isDesktop, setIsDesktop] = useState(false);
  const rafRef = useRef(null);
  const startedRef = useRef(false);

  const measure = useCallback(() => {
    const wrap = wrapRef.current;
    if (!wrap) return;
    const box = wrap.getBoundingClientRect();
    const points = nodeRefs.current.map((el) => {
      const r = el.getBoundingClientRect();
      return [r.left - box.left + r.width / 2, r.top - box.top + r.height / 2];
    });
    const dists = [0];
    for (let i = 1; i < points.length; i++) {
      const [x1, y1] = points[i - 1];
      const [x2, y2] = points[i];
      dists.push(dists[i - 1] + Math.hypot(x2 - x1, y2 - y1));
    }
    setGeo({ points, dists, total: dists[dists.length - 1] || 0, width: box.width, height: box.height });
    setIsDesktop(window.matchMedia("(min-width: 1024px)").matches);
  }, []);

  useLayoutEffect(() => {
    measure();
    const ro = new ResizeObserver(measure);
    if (wrapRef.current) ro.observe(wrapRef.current);
    window.addEventListener("resize", measure);
    return () => {
      ro.disconnect();
      window.removeEventListener("resize", measure);
    };
  }, [measure]);

  const play = useCallback(() => {
    cancelAnimationFrame(rafRef.current);
    if (reduceMotion) {
      setProgress(1);
      return;
    }
    let t0 = null;
    const tick = (ts) => {
      if (t0 === null) t0 = ts;
      const p = Math.min(1, (ts - t0) / DURATION);
      setProgress(p);
      if (p < 1) rafRef.current = requestAnimationFrame(tick);
    };
    setProgress(0);
    rafRef.current = requestAnimationFrame(tick);
  }, [reduceMotion]);

  // Play once, the first time the path scrolls into view.
  useEffect(() => {
    const el = wrapRef.current;
    if (!el) return undefined;
    const io = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !startedRef.current) {
          startedRef.current = true;
          play();
        }
      },
      { threshold: 0.35 }
    );
    io.observe(el);
    return () => {
      io.disconnect();
      cancelAnimationFrame(rafRef.current);
    };
  }, [play]);

  const { points, dists, total } = geo;
  const travelled = progress * total;
  const pointAt = (d) => {
    for (let i = 1; i < points.length; i++) {
      if (d <= dists[i]) {
        const t = (d - dists[i - 1]) / (dists[i] - dists[i - 1] || 1);
        const [x1, y1] = points[i - 1];
        const [x2, y2] = points[i];
        return [x1 + (x2 - x1) * t, y1 + (y2 - y1) * t];
      }
    }
    return points[points.length - 1] || [0, 0];
  };
  const [dotX, dotY] = points.length ? pointAt(travelled) : [0, 0];
  const pathD = points.map(([x, y], i) => `${i ? "L" : "M"}${x.toFixed(1)} ${y.toFixed(1)}`).join(" ");

  const litCount = dists.filter((d) => travelled >= d - 1).length;
  const lastLit = litCount - 1;
  const activePhase = lastLit >= 0 ? steps[lastLit].phase : -1;

  const step = steps[selected];
  const SelIcon = ICONS[selected];

  return (
    <div>
      <div className="flex flex-wrap gap-2 mb-10">
        {phases.map((p, i) => (
          <span
            key={p}
            className={`font-mono text-[11px] tracking-[0.12em] px-3.5 py-1.5 rounded-full border transition-colors duration-500 ${
              activePhase === i ? "bg-ink text-white border-ink" : "bg-white text-zinc-500 border-zinc-200"
            }`}
          >
            0{i + 1} {p}
          </span>
        ))}
      </div>

      <div ref={wrapRef} className="relative">
        <svg
          className="pointer-events-none absolute inset-0 overflow-visible"
          width={geo.width}
          height={geo.height}
          aria-hidden="true"
        >
          {points.length > 1 && (
            <>
              <path d={pathD} fill="none" stroke="#d4d4d8" strokeWidth="2" strokeDasharray="3 7" strokeLinecap="round" strokeLinejoin="round" />
              <path
                d={pathD}
                fill="none"
                stroke="#e11d48"
                strokeWidth="2.5"
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeDasharray={total}
                strokeDashoffset={total - travelled}
              />
              {progress > 0 && progress < 1 && (
                <>
                  <circle cx={dotX} cy={dotY} r="12" fill="#e11d48" opacity="0.18" />
                  <circle cx={dotX} cy={dotY} r="5.5" fill="#e11d48" />
                </>
              )}
            </>
          )}
        </svg>

        <ol className="relative grid grid-cols-1 lg:grid-cols-4 gap-y-16 lg:gap-y-24 lg:gap-x-12 max-w-xs mx-auto lg:max-w-none">
          {steps.map((s, i) => {
            const Icon = ICONS[i];
            const lit = i <= lastLit;
            const edge = i === 0 || i === steps.length - 1;
            const [col, row] = GRID[i];
            return (
              <li
                key={s.title}
                ref={(el) => (nodeRefs.current[i] = el)}
                style={isDesktop ? { gridColumn: col, gridRow: row } : undefined}
              >
                <button
                  type="button"
                  onClick={() => setSelected(i)}
                  aria-pressed={selected === i}
                  className={`w-full h-[104px] rounded-[20px] border flex flex-col items-center justify-center gap-1.5 text-center px-3 transition-all duration-500 ease-out-expo ${
                    lit
                      ? edge
                        ? "bg-gradient-to-br from-rose-start to-rose border-rose text-white -translate-y-1 shadow-[0_0_0_5px_rgba(225,29,72,0.15)]"
                        : "bg-ink border-ink text-white -translate-y-1 shadow-[0_0_0_5px_rgba(225,29,72,0.15)]"
                      : i === 0
                      ? "bg-rose-soft border-rose/30 text-ink"
                      : "bg-white border-zinc-200 text-ink hover:border-zinc-400"
                  } ${selected === i ? "ring-2 ring-rose ring-offset-4 ring-offset-white" : ""}`}
                >
                  <Icon size={22} className={`transition-colors duration-500 ${lit ? (edge ? "text-white" : "text-rose-light") : "text-ink"}`} />
                  <span className="text-[13px] font-extrabold leading-tight">{s.title}</span>
                  <span className={`font-mono text-[9.5px] tracking-[0.12em] transition-colors duration-500 ${lit ? (edge ? "text-white/85" : "text-rose-light") : "text-zinc-400"}`}>
                    {s.phase >= 0 ? phases[s.phase] : s.label}
                  </span>
                </button>
              </li>
            );
          })}
        </ol>

        {points.length > 1 &&
          CHECKPOINT_AFTER.map((after, k) => {
            const [x1, y1] = points[after];
            const [x2, y2] = points[after + 1];
            const mx = (x1 + x2) / 2;
            const my = (y1 + y2) / 2;
            const vertical = Math.abs(x2 - x1) < Math.abs(y2 - y1);
            let transform = "translate(-50%, -50%)";
            if (isDesktop && vertical) {
              transform = mx > geo.width / 2 ? "translate(calc(-100% - 16px), -50%)" : "translate(16px, -50%)";
            } else if (isDesktop) {
              transform = "translate(-50%, 70px)";
            }
            const on = travelled >= (dists[after] + dists[after + 1]) / 2;
            const Icon = CHECKPOINT_ICONS[k];
            return (
              <div
                key={k}
                className={`absolute z-10 flex items-center gap-2 whitespace-nowrap rounded-full border px-3.5 py-2 text-xs font-bold transition-colors duration-500 ${
                  on ? "bg-rose border-rose text-white" : "bg-white border-rose/30 text-rose-dark"
                }`}
                style={{ left: mx, top: my, transform }}
              >
                <Icon size={13} />
                {checkpoints[k]}
              </div>
            );
          })}
      </div>

      <div className="mt-16 flex flex-col sm:flex-row sm:items-center gap-5 rounded-2xl border border-zinc-200 bg-white p-6">
        <div className="w-12 h-12 rounded-2xl bg-ink text-rose-light flex items-center justify-center flex-shrink-0">
          <SelIcon size={22} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-mono text-[11px] tracking-[0.12em] text-rose-dark">
            {step.phase >= 0 ? phases[step.phase] : step.label}
          </div>
          <div className="text-lg font-extrabold text-ink mt-0.5">{step.title}</div>
          <p className="text-zinc-500 leading-relaxed mt-1">{step.desc}</p>
        </div>
        <div className="flex flex-col items-start sm:items-end gap-2">
          <button
            type="button"
            onClick={play}
            className="inline-flex items-center gap-2 rounded-xl border border-zinc-300 px-4 py-2 text-sm font-bold text-ink hover:border-ink transition-colors"
          >
            <RotateCcw size={14} className="rtl:-scale-x-100" /> {replayLabel}
          </button>
          <span className="text-xs text-zinc-400">{hint}</span>
        </div>
      </div>
    </div>
  );
};

export default WorkflowPath;
